import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ListSleepScreens,
  FindSchemasBySleepScreenId,
  AssignSleepScreenToSchemas,
} from '../../libs/GraphQL';
import { SYSTEM_DEFAULT_SET } from './sleepScreenHelpers';
import { isActiveFridge, isBaseSchema } from '../../utils/schemaLifecycle';
import { getCachedFleet } from '../../utils/activeSchemaIds';
import { DEFAULT_SLEEP_SCREEN_ID } from '../../constants/baseSchemas';
import AssignFridgePicker from './AssignFridgePicker';
import AssignConfirmModal from './AssignConfirmModal';
import AssignSuccess from './AssignSuccess';
import SleepScreenVideoPreview from './SleepScreenVideoPreview';
import styles from './SleepScreenAssign.module.css';

const SLOTS = [
  { key: 'standard', label: 'Standard', enabled: true },
  {
    key: 'happyHourAm',
    label: 'AM Happy Hour',
    enabled: false,
    disabledReason: 'AM Happy Hour assignment is coming in a later release.',
  },
  {
    key: 'happyHourPm',
    label: 'PM Happy Hour',
    enabled: false,
    disabledReason: 'PM Happy Hour assignment is coming in a later release.',
  },
];

const slotLabelFor = (slotKey) => {
  const found = SLOTS.find((s) => s.key === slotKey);
  return found ? found.label : slotKey;
};

const slotOptionClass = (slot, currentSlot) => {
  if (!slot.enabled) return styles.radioOptionDisabled;
  if (currentSlot === slot.key) return styles.radioOptionActive;
  return styles.radioOption;
};

const readEntryParam = (search) => {
  const params = new URLSearchParams(search);
  return params.get('entry') || '';
};

const SleepScreenAssign = () => {
  const location = useLocation();
  const entryFromUrl = readEntryParam(location.search);

  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(true);
  const [selectedEntryId, setSelectedEntryId] = useState(entryFromUrl);
  const [selectedSlot, setSelectedSlot] = useState('standard');

  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(true);

  const [currentAssignments, setCurrentAssignments] = useState([]);
  const [currentAssignmentsLoading, setCurrentAssignmentsLoading] =
    useState(false);

  const [selection, setSelection] = useState(() => new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [successResult, setSuccessResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchEntries = async () => {
      setEntriesLoading(true);
      try {
        const result = await ListSleepScreens();
        if (cancelled) return;
        setEntries(result || []);
      } catch (err) {
        if (cancelled) return;
        console.error('listSleepScreens failed:', err);
        toast.error('Failed to load sleep screen library.');
      } finally {
        if (!cancelled) setEntriesLoading(false);
      }
    };
    fetchEntries();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchRoster = async () => {
      setRosterLoading(true);
      try {
        const result = await getCachedFleet();
        if (cancelled) return;
        const normalized = (result || [])
          .map((loc) => {
            const statusStr = typeof loc.status === 'string' ? loc.status : '';
            return {
              schemaId: loc.id,
              isActive: isActiveFridge(loc),
              statusLabel: statusStr || null,
              vertical: loc.vertical || null,
              region: loc.region || null,
              market: loc.market || null,
              cluster: loc.cluster || null,
            };
          })
          .filter((f) => f.schemaId)
          .sort((a, b) => a.schemaId.localeCompare(b.schemaId));
        setRoster(normalized);
      } catch (err) {
        if (cancelled) return;
        console.error('getCachedFleet failed:', err);
        toast.error('Failed to load fleet roster.');
      } finally {
        if (!cancelled) setRosterLoading(false);
      }
    };
    fetchRoster();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedEntryId) {
      setCurrentAssignments([]);
      return undefined;
    }
    let cancelled = false;
    const fetchCurrent = async () => {
      setCurrentAssignmentsLoading(true);
      try {
        const rows = await FindSchemasBySleepScreenId(selectedEntryId);
        if (cancelled) return;
        const onThisSlot = (rows || [])
          .filter((r) => r.slot === selectedSlot)
          .map((r) => r.schemaId);
        setCurrentAssignments(onThisSlot);
      } catch (err) {
        if (cancelled) return;
        console.error('findSchemasBySleepScreenId failed:', err);
        setCurrentAssignments([]);
      } finally {
        if (!cancelled) setCurrentAssignmentsLoading(false);
      }
    };
    fetchCurrent();
    return () => {
      cancelled = true;
    };
  }, [selectedEntryId, selectedSlot]);

  const selectedEntry = useMemo(
    () => entries.find((e) => e.sleepScreenId === selectedEntryId) || null,
    [entries, selectedEntryId],
  );

  const sortedEntries = useMemo(
    () =>
      entries.slice().sort((a, b) => {
        const nameA = (a.displayName || a.sleepScreenId || '').toLowerCase();
        const nameB = (b.displayName || b.sleepScreenId || '').toLowerCase();
        return nameA.localeCompare(nameB);
      }),
    [entries],
  );

  const alreadyOnThisSlotSet = useMemo(
    () => new Set(currentAssignments),
    [currentAssignments],
  );

  const newSelectionIds = useMemo(
    () => Array.from(selection).filter((id) => !alreadyOnThisSlotSet.has(id)),
    [selection, alreadyOnThisSlotSet],
  );

  const canOpenConfirm = Boolean(selectedEntryId) && newSelectionIds.length > 0;

  // Base/master schemas must stay on the default sleep screen — a copy made
  // from a base that points at a specific screen drifts off the default. Pasting
  // or uploading base ids and assigning a non-default entry is the path that
  // breaks this, so flag it before the user commits.
  const baseSchemasInSelection = useMemo(
    () => newSelectionIds.filter(isBaseSchema),
    [newSelectionIds],
  );
  const baseSchemaDriftRisk =
    baseSchemasInSelection.length > 0 &&
    selectedEntryId !== DEFAULT_SLEEP_SCREEN_ID;

  const handleConfirm = async () => {
    // Snapshot the inputs at submission time so the success screen reflects
    // what was actually sent, not whatever the user fiddled with mid-mutation.
    const submittedIds = newSelectionIds.slice();
    const submittedEntry = selectedEntry || {
      sleepScreenId: selectedEntryId,
      displayName: selectedEntryId,
    };
    const submittedSlot = selectedSlot;
    try {
      const result = await AssignSleepScreenToSchemas({
        sleepScreenId: selectedEntryId,
        slot: selectedSlot,
        schemaIds: submittedIds,
      });
      setSuccessResult({
        successfulCount: result.successfulCount,
        errors: result.errors || [],
        attemptedCount: submittedIds.length,
        entry: submittedEntry,
        slot: submittedSlot,
      });
      setShowConfirm(false);
      if (result.errors && result.errors.length) {
        toast.warn(
          `Assignment complete with ${result.errors.length} failure${
            result.errors.length === 1 ? '' : 's'
          }.`,
        );
      } else {
        toast.success('Assignment complete.');
      }
    } catch (err) {
      console.error('assignSleepScreenToSchemas failed:', err);
      const msg =
        (err && (err.errorMessage || err.message)) ||
        'Failed to assign fridges.';
      if (err && err.partial && err.partial.successfulCount > 0) {
        toast.error(
          `${msg} (${err.partial.successfulCount} fridges already written before failure.)`,
        );
      } else {
        toast.error(msg);
      }
    }
  };

  const handleAssignAnother = () => {
    setSelectedEntryId('');
    setSelectedSlot('standard');
    setSelection(new Set());
    setCurrentAssignments([]);
    setSuccessResult(null);
  };

  if (successResult) {
    return (
      <main className={styles.container}>
        <nav className={styles.breadcrumb} aria-label="Breadcrumb">
          <Link to="/sleep-screens" className={styles.crumbLink}>
            Sleep Screen Library
          </Link>
          <span className={styles.crumbSep} aria-hidden="true">
            ›
          </span>
          <span className={styles.crumbCurrent}>Assign Fridges</span>
        </nav>
        <AssignSuccess
          entry={successResult.entry}
          slotLabel={slotLabelFor(successResult.slot)}
          successfulCount={successResult.successfulCount}
          attemptedCount={successResult.attemptedCount}
          errors={successResult.errors}
          onAssignAnother={handleAssignAnother}
        />
      </main>
    );
  }

  const isSystemDefault = selectedEntry
    ? SYSTEM_DEFAULT_SET.has(selectedEntry.sleepScreenId)
    : false;

  return (
    <main className={styles.container}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/sleep-screens" className={styles.crumbLink}>
          Sleep Screen Library
        </Link>
        <span className={styles.crumbSep} aria-hidden="true">
          ›
        </span>
        <span className={styles.crumbCurrent}>Assign Fridges</span>
      </nav>

      <div className={styles.wizard}>
        <aside className={styles.leftCol}>
          <div className={styles.summary}>
            <div className={styles.summaryTitle}>Summary</div>
            <dl className={styles.summaryGrid}>
              <dt>Entry</dt>
              <dd>
                {selectedEntry
                  ? selectedEntry.displayName || selectedEntry.sleepScreenId
                  : '—'}
              </dd>
              <dt>Slot</dt>
              <dd>{slotLabelFor(selectedSlot)}</dd>
              <dt>Fridges selected</dt>
              <dd>{newSelectionIds.length.toLocaleString()}</dd>
            </dl>
          </div>
        </aside>

        <section className={styles.mainCol}>
          <div className={styles.stepPanel}>
            <h2 className={styles.stepHeading}>
              <span className={styles.stepNum}>1</span> Choose entry
            </h2>
            <p className={styles.stepHelper}>
              Pick the sleep screen entry you want to assign to fridges.
            </p>
            <select
              className={styles.select}
              value={selectedEntryId}
              disabled={entriesLoading}
              onChange={(e) => {
                setSelectedEntryId(e.target.value);
                setSelection(new Set());
              }}
              aria-label="Select sleep screen entry"
            >
              <option value="">
                {entriesLoading ? 'Loading entries…' : '— select an entry —'}
              </option>
              {sortedEntries.map((entry) => (
                <option key={entry.sleepScreenId} value={entry.sleepScreenId}>
                  {entry.displayName || entry.sleepScreenId} (
                  {entry.sleepScreenId})
                </option>
              ))}
            </select>
            {selectedEntry && (
              <div className={styles.entryPreview}>
                <SleepScreenVideoPreview url={selectedEntry.videoUrl} />
                <div className={styles.entryPreviewMeta}>
                  <div className={styles.entryPreviewName}>
                    {selectedEntry.displayName || selectedEntry.sleepScreenId}
                  </div>
                  <div className={styles.entryPreviewId}>
                    {selectedEntry.sleepScreenId}
                  </div>
                  {selectedEntry.description && (
                    <p className={styles.entryPreviewDescription}>
                      {selectedEntry.description}
                    </p>
                  )}
                </div>
              </div>
            )}
            {isSystemDefault && (
              <div className={styles.warningBlock}>
                <strong>Heads up:</strong> {selectedEntry.displayName} is a
                system default. Assigning it directly tags fridges with this
                catalog ID — the video they actually play follows whatever this
                entry currently points to.
              </div>
            )}
          </div>

          <div className={styles.stepPanel}>
            <h2 className={styles.stepHeading}>
              <span className={styles.stepNum}>2</span> Choose slot
            </h2>
            <p className={styles.stepHelper}>
              Which slot on each fridge should play this sleep screen?
            </p>
            <div className={styles.radioGroup} role="radiogroup">
              {SLOTS.map((slot) => (
                <label
                  key={slot.key}
                  htmlFor={`slot-${slot.key}`}
                  className={slotOptionClass(slot, selectedSlot)}
                  title={!slot.enabled ? slot.disabledReason : undefined}
                >
                  <input
                    id={`slot-${slot.key}`}
                    type="radio"
                    name="slot"
                    value={slot.key}
                    checked={selectedSlot === slot.key}
                    disabled={!slot.enabled}
                    onChange={(e) => {
                      setSelectedSlot(e.target.value);
                      setSelection(new Set());
                    }}
                  />
                  <span>{slot.label}</span>
                  {!slot.enabled && (
                    <span className={styles.disabledTag}>coming soon</span>
                  )}
                </label>
              ))}
            </div>
          </div>

          <div className={styles.stepPanel}>
            <h2 className={styles.stepHeading}>
              <span className={styles.stepNum}>3</span> Choose fridges
            </h2>
            <p className={styles.stepHelper}>
              Pick the fridges to assign. Fridges already on this slot are shown
              as <strong>Current</strong> and can&apos;t be deselected.
            </p>
            {!selectedEntryId && (
              <div className={styles.stateBlock}>
                Choose an entry above to see the fleet picker.
              </div>
            )}
            {selectedEntryId && currentAssignmentsLoading && (
              <div className={styles.stateBlock}>
                Checking current assignments…
              </div>
            )}
            {selectedEntryId && !currentAssignmentsLoading && (
              <AssignFridgePicker
                roster={roster}
                rosterLoading={rosterLoading}
                currentAssignments={currentAssignments}
                selection={selection}
                onSelectionChange={setSelection}
              />
            )}
          </div>

          {baseSchemaDriftRisk && (
            <div className={styles.warningBlock}>
              <strong>Heads up:</strong> {baseSchemasInSelection.length} of the
              selected{' '}
              {baseSchemasInSelection.length === 1 ? 'id is a' : 'ids are'} base
              schema
              {baseSchemasInSelection.length === 1 ? '' : 's'} (
              {baseSchemasInSelection.slice(0, 5).join(', ')}
              {baseSchemasInSelection.length > 5
                ? `, +${baseSchemasInSelection.length - 5} more`
                : ''}
              ). Base schemas should stay on the default sleep screen — fridges
              copied from them inherit this assignment, so a non-default screen
              here drifts every future copy off the default.
            </div>
          )}

          <div className={styles.actionBar}>
            <Link to="/sleep-screens" className={styles.secondaryLink}>
              Cancel
            </Link>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => setShowConfirm(true)}
              disabled={!canOpenConfirm}
            >
              Review &amp; assign
            </button>
          </div>
        </section>
      </div>

      <AssignConfirmModal
        show={showConfirm}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        entry={selectedEntry}
        slotLabel={slotLabelFor(selectedSlot)}
        newCount={newSelectionIds.length}
      />
    </main>
  );
};

export default SleepScreenAssign;
